import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { buildApiUrl } from '../../config/api';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LocalSpinner = styled.div<{ $size?: number }>`
  width: ${props => props.$size || 24}px;
  height: ${props => props.$size || 24}px;
  border: 3px solid rgba(93, 114, 44, 0.2);
  border-top-color: #5D722C;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const CenterLoader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  width: 100%;
  color: #5D722C;
  font-size: 13px;
  font-weight: 500;
  gap: 12px;
`;

const SpinnerOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 10;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 10px 0;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  padding: 0 0 10px;
  color: #22334d;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  flex-wrap: wrap;
  user-select: none;
`;

const SectionLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #34495e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ActionButton = styled.button`
  padding: 7px 10px;
  border: 1.5px solid #b8cc8a;
  border-radius: 6px;
  background: #eaf4d6;
  color: #2f4a1c;
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  transition: all 0.12s;

  &:hover {
    background: #dcebbd;
    border-color: #9fbc69;
  }
`;

const Card = styled.div<{ $isLoading?: boolean }>`
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px;
  cursor: ${props => (props.$isLoading ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.$isLoading ? 0.7 : 1)};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;

  &:hover {
    border-color: #5D722C;
    background: white;
    transform: translateY(-2px);
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 4px;
  object-fit: contain;
`;

const Name = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #334155;
  text-align: center;
  line-height: 1.2;
`;

const ThumbWrap = styled.div`
  width: 100%;
  aspect-ratio: 1.1 / 1;
  border-radius: 4px;
  background: transparent;
  position: relative;
  overflow: hidden;
`;


const LoadingLabel = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 2px;
`;

interface EsqTemplate {
  id: number;
  name: string;
  image_path: string;
}

interface EsqTemplatesProps {
  modalId: number;
  colorId: number;
  onSelect: (template: EsqTemplate) => void;
  onClose?: () => void;
}

const EsqTemplates: React.FC<EsqTemplatesProps> = ({ modalId, colorId, onSelect, onClose }) => {
  const [templates, setTemplates] = useState<EsqTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageStatus, setImageStatus] = useState<Record<number, 'loading' | 'loaded' | 'error'>>({});
  const setThumbStatus = (id: number, status: 'loading' | 'loaded' | 'error') => {
    setImageStatus(prev => (prev[id] === status ? prev : { ...prev, [id]: status }));
  };

  useEffect(() => {
    if (!modalId || !colorId) return;
    setLoading(true);
    fetch(buildApiUrl(`/api/esq/templates/${modalId}/${colorId}`))
      .then(res => res.json())
      .then(data => {
        const nextTemplates = Array.isArray(data)
          ? [...data].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
          : [];
        setTemplates(nextTemplates);
        const nextStatus: Record<number, 'loading' | 'loaded' | 'error'> = {};
        nextTemplates.forEach((tpl: EsqTemplate) => {
          nextStatus[tpl.id] = 'loading';
        });
        setImageStatus(nextStatus);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch ESQ templates:', err);
        setLoading(false);
      });
  }, [modalId, colorId]);

  if (!modalId || !colorId) return <div style={{ padding: '20px', textAlign: 'center' }}>Please select a modal and color first.</div>;
  if (loading) return (
    <CenterLoader>
      <LocalSpinner $size={32} />
      <span>Loading templates...</span>
    </CenterLoader>
  );

  return (
    <div style={{ position: 'relative' }}>
      <Breadcrumbs>
        <span className="crumb" style={{ cursor: 'pointer', color: '#5D722C', fontWeight: 500 }} onClick={onClose}>Select Model</span>
        <span style={{ color: '#b0b7c3', margin: '0 2px' }}>{'>'}</span>
        <span className="current" style={{ fontWeight: 600 }}>Select Template</span>
      </Breadcrumbs>

      <SectionLabel>Select Template</SectionLabel>

      <Grid>
        {templates.map(tpl => (
          <Card
            key={tpl.id}
            $isLoading={(imageStatus[tpl.id] || 'loading') === 'loading'}
            onClick={() => {
              if ((imageStatus[tpl.id] || 'loading') === 'loading') return;
              onSelect(tpl);
            }}
          >
            <ThumbWrap>
              <Image
                src={buildApiUrl('/api/uploads/' + tpl.image_path)}
                alt={tpl.name}
                ref={(img) => {
                  if (!img) return;
                  if (img.complete) {
                    setThumbStatus(tpl.id, img.naturalWidth > 0 ? 'loaded' : 'error');
                  }
                }}
                onLoad={() => setThumbStatus(tpl.id, 'loaded')}
                onError={() => setThumbStatus(tpl.id, 'error')}
              />
              {(imageStatus[tpl.id] || 'loading') === 'loading' && (
                <SpinnerOverlay>
                  <LocalSpinner $size={20} />
                </SpinnerOverlay>
              )}
            </ThumbWrap>
            <Name>{tpl.name}</Name>
          </Card>
        ))}
      </Grid>

      {onClose && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <ActionButton onClick={onClose}>
            Cancel
          </ActionButton>
        </div>
      )}
    </div>
  );
};

export default EsqTemplates;
